import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchCalendario } from '../../lib/api';
import { formatRangeSettimana } from '../../lib/weekFormat';

const MONTH_LABELS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI_IT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

const DOT_COLORS = {
  vuoto: 'var(--qp-border)',
  tutte_da_fare: 'var(--qp-text-mute)',
  parziale: '#F59E0B',     // ambra
  tutte_fatte: 'var(--qp-success)',  // verde
};

// Ambra usata per evidenziare selezione (giorno/settimana/mese) senza clash con
// il nero "oggi": bordo pieno #F59E0B, background tenue con alpha per funzionare
// sia in tema chiaro che scuro.
const SELECT_BG = 'rgba(245, 158, 11, 0.22)';
const SELECT_BG_STRONG = 'rgba(245, 158, 11, 0.45)';
const SELECT_BORDER = '#F59E0B';

/**
 * Estrae il "target selezionato" dalla route corrente.
 * Ritorna { day, week, month } dove ogni campo è la stringa target o null.
 */
function parseSelectionFromPath(pathname) {
  const out = { day: null, week: null, month: null };
  let m;
  if ((m = pathname.match(/\/periodo\/giorno\/(\d{4}-\d{2}-\d{2})/))) out.day = m[1];
  else if ((m = pathname.match(/\/periodo\/settimana\/(\d{4}-W\d{2})/))) out.week = m[1];
  else if ((m = pathname.match(/\/periodo\/mese\/(\d{4}-\d{2})/))) out.month = m[1];
  return out;
}

/**
 * Mini-calendario nel fondo della sidebar (solo quando espansa).
 * - Frecce per cambiare mese
 * - Click giorno → /periodo/giorno/YYYY-MM-DD (evidenziato giallo)
 * - Click numero settimana → /periodo/settimana/YYYY-Www (evidenzia i 7 giorni in giallo)
 * - Click intestazione mese → /periodo/mese/YYYY-MM (evidenzia i giorni in-mese in giallo)
 * - Oggi resta sempre in nero (anche dentro una settimana/mese selezionati)
 * - Puntino sotto giorno (stato MIT) + marker scadenze KR
 */
export default function MiniCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);  // 1-12
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const selection = parseSelectionFromPath(location.pathname);

  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const currentMonthKey = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    fetchCalendario(year, month).then(setData).catch(() => setData(null));
  }, [year, month]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  const isMonthSelected = selection.month === currentMonthKey;

  return (
    <div className="px-2 py-3 border-t border-line" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          onClick={prev}
          className="p-1 text-soft hover:text-ink hover:bg-linesoft"
          data-testid="mini-cal-prev"
          aria-label="Mese precedente"
        >
          <ChevronLeft size={12} strokeWidth={1.5} />
        </button>
        <Link
          to={`/periodo/mese/${currentMonthKey}`}
          className="font-mono text-[10px] uppercase tracking-[0.18em] hover:text-ink px-2 py-0.5 border transition-colors"
          style={isMonthSelected ? {
            color: SELECT_BORDER,
            borderColor: SELECT_BORDER,
            backgroundColor: SELECT_BG,
          } : {
            color: 'var(--qp-text-soft)',
            borderColor: 'transparent',
          }}
          data-testid="mini-cal-title"
          title="Vai alla vista Mese"
        >
          {MONTH_LABELS_IT[month - 1].slice(0, 3)} {year}
        </Link>
        <button
          onClick={next}
          className="p-1 text-soft hover:text-ink hover:bg-linesoft"
          data-testid="mini-cal-next"
          aria-label="Mese successivo"
        >
          <ChevronRight size={12} strokeWidth={1.5} />
        </button>
      </div>

      {/* Header giorni: W è chiaramente una colonna cliccabile */}
      <div className="grid grid-cols-8 gap-[2px] mb-1">
        <span
          className="text-[8px] text-mute font-mono text-center uppercase tracking-wider"
          title="Numero settimana ISO · clicca per selezionare"
        >
          W
        </span>
        {GIORNI_IT.map((g, i) => (
          <span key={`g-${i}-${g}`} className="text-[8px] text-mute font-mono text-center">{g}</span>
        ))}
      </div>

      {/* Settimane */}
      <div className="space-y-[2px]">
        {(data?.weeks || []).map((w) => {
          const wNum = w.settimana.split('-W')[1];
          const isWeekSelected = selection.week === w.settimana;
          return (
            <div key={w.settimana} className="grid grid-cols-8 gap-[2px]">
              <Link
                to={`/periodo/settimana/${w.settimana}`}
                className="text-[9px] font-mono text-center py-1 leading-none border transition-colors hover:border-ink hover:text-ink"
                style={isWeekSelected ? {
                  color: SELECT_BORDER,
                  borderColor: SELECT_BORDER,
                  backgroundColor: SELECT_BG_STRONG,
                  fontWeight: 700,
                } : {
                  color: 'var(--qp-text-mute)',
                  borderColor: 'var(--qp-border-soft)',
                }}
                data-testid={`mini-cal-week-${w.settimana}`}
                title={`Settimana W${wNum} · ${formatRangeSettimana(w.settimana)} · clicca per aprire`}
              >
                {wNum}
              </Link>
              {w.giorni.map((g) => {
                const isToday = g.data === todayIso;
                const isDaySelected = selection.day === g.data;
                const isInSelectedWeek = isWeekSelected;
                const isInSelectedMonth = isMonthSelected && g.in_mese;
                // Oggi vince sempre visualmente (rimane nero).
                const highlightYellow = !isToday && (isDaySelected || isInSelectedWeek || isInSelectedMonth);
                const dot = DOT_COLORS[g.stato] || 'var(--qp-border)';
                const hasScad = (g.scadenze || []).length > 0;
                const baseCls =
                  'relative flex flex-col items-center justify-center py-1 text-[10px] leading-none transition-colors';
                let cls = baseCls;
                let inline = {};
                if (isToday) {
                  cls += ' bg-ink text-canvas font-bold';
                } else if (highlightYellow) {
                  cls += ' font-semibold';
                  inline = {
                    backgroundColor: isDaySelected ? SELECT_BG_STRONG : SELECT_BG,
                    color: 'var(--qp-ink)',
                    outline: isDaySelected ? `1px solid ${SELECT_BORDER}` : 'none',
                  };
                } else if (g.in_mese) {
                  cls += ' text-ink hover:bg-linesoft';
                } else {
                  cls += ' text-line hover:bg-linesoft';
                }
                return (
                  <button
                    key={g.data}
                    onClick={() => navigate(`/periodo/giorno/${g.data}`)}
                    className={cls}
                    style={inline}
                    data-testid={`mini-cal-day-${g.data}`}
                    title={`${g.data}${g.mit_totale ? ` · ${g.mit_fatte}/${g.mit_totale} MIT` : ''}${hasScad ? ' · scadenza' : ''}`}
                  >
                    <span>{g.giorno}</span>
                    {g.mit_totale > 0 && (
                      <span
                        className="inline-block w-1 h-1 rounded-full mt-0.5"
                        style={{ backgroundColor: dot }}
                      />
                    )}
                    {hasScad && (
                      <span
                        className="absolute top-0 right-0 w-1 h-1 bg-accent"
                        style={{ borderRadius: '50%' }}
                        aria-hidden
                      />
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Hint UX: rende esplicite le tre modalità di selezione. */}
      <p className="mt-2 text-[8px] text-mute font-mono leading-tight px-0.5" data-testid="mini-cal-hint">
        Clicca <span className="text-ink">giorno</span>, <span className="text-ink">W</span> per settimana o <span className="text-ink">mese</span> per selezionare.
      </p>
    </div>
  );
}
